/** @jsxImportSource @emotion/react */
import {
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableFooter as MuiTableFooter,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TableSortLabel as MuiTableSortLabel,
  Paper,
} from '@mui/material';
import { Loading } from '@zip/business-components';
import React, { useMemo, useState } from 'react';
import * as styles from './SortableTable.styles';
import { SortableTableColumn, SortableTableProps } from './SortableTableProps';

const SortableTable = <TData,>({
  data,
  columns,
  isLoading,
  emptyMessage,
  ...props
}: SortableTableProps<TData>): React.ReactElement => {
  const [sortingColumn, setSortingColumn] =
    useState<SortableTableColumn<TData>>(undefined);
  const [sortingStyle, setSortingStyle] = useState<'asc' | 'desc'>('asc');

  const dataSorted = useMemo(() => {
    const sorted = [...data].sort((lhs, rhs) => {
      if (!sortingColumn) return 0;

      const lhsKey = lhs[sortingColumn.name];
      const rhsKey = rhs[sortingColumn.name];

      if (sortingColumn.customComparer) {
        const compare = sortingColumn.customComparer(lhs, rhs);
        return sortingStyle === 'asc' ? compare : -compare;
      }

      if (lhsKey === rhsKey) return 0;

      if (sortingStyle === 'asc') {
        return lhsKey < rhsKey ? -1 : 1;
      } else {
        return lhsKey < rhsKey ? 1 : -1;
      }
    });
    return sorted;
  }, [data, isLoading, sortingColumn, sortingStyle]);

  const isSortingColumn = (colume: SortableTableColumn<TData>): boolean =>
    sortingColumn && colume.name === sortingColumn.name;

  return (
    <MuiTableContainer component={Paper} elevation={0} {...props}>
      <MuiTable id="sortable-table" stickyHeader>
        {/* Head */}
        <MuiTableHead css={styles.tableHead}>
          <MuiTableRow>
            {columns.map((column, idx) =>
              column.sortable ? (
                <MuiTableCell key={idx}>
                  <MuiTableSortLabel
                    active
                    css={styles.sortLabel(isSortingColumn(column))}
                    direction={isSortingColumn(column) ? sortingStyle : 'asc'}
                    onClick={(): void => {
                      setSortingColumn(column);
                      const updatedSortDirection =
                        sortingStyle === 'asc' ? 'desc' : 'asc';
                      setSortingStyle(updatedSortDirection);
                      column.onSort?.(
                        column.name?.toString() ?? '',
                        updatedSortDirection
                      );
                    }}
                  >
                    {column.makeColumn()}
                  </MuiTableSortLabel>
                </MuiTableCell>
              ) : (
                <MuiTableCell key={idx}>{column.makeColumn()}</MuiTableCell>
              )
            )}
          </MuiTableRow>
        </MuiTableHead>

        {/* Body */}
        <MuiTableBody css={styles.tableBody}>
          {dataSorted.map((datum, ridx) => (
            <MuiTableRow key={ridx}>
              {columns.map((col, cidx) => (
                <MuiTableCell key={cidx}>{col.makeCell(datum)}</MuiTableCell>
              ))}
            </MuiTableRow>
          ))}
        </MuiTableBody>

        {/* Footer */}
        <MuiTableFooter>
          {isLoading && (
            <MuiTableRow>
              <MuiTableCell colSpan={columns?.length ?? 1}>
                <Loading width={80} height={30} />
              </MuiTableCell>
            </MuiTableRow>
          )}
          {!isLoading && data.length === 0 && (
            <MuiTableRow>
              <MuiTableCell colSpan={columns?.length ?? 1}>
                <span className="body4">{emptyMessage}</span>
              </MuiTableCell>
            </MuiTableRow>
          )}
        </MuiTableFooter>
      </MuiTable>
    </MuiTableContainer>
  );
};

export default SortableTable;
